/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 */

import { LRUCache } from 'lru-cache';

type Timeout = ReturnType<typeof setTimeout>;

const fnTimers = new Map<string, Timeout>();

export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  uniqueKey: string,
  timeDelay = 1500
) {
  return (...args: Parameters<T>) => {
    const fnTimer = fnTimers.get(uniqueKey);
    if (fnTimer) {
      clearTimeout(fnTimer);
    }
    fnTimers.set(
      uniqueKey,
      setTimeout(() => {
        return fn(...args);
      }, timeDelay)
    );
  };
}

const throttleTimers = new Map<string, boolean>();

export function throttle<T extends (...args: never[]) => unknown>(
  fn: T,
  uniqueKey: string,
  timeDelay = 1500
): (...args: Parameters<T>) => void {
  return async (...args: Parameters<T>) => {
    if (!throttleTimers.get(uniqueKey)) {
      // Execute the function immediately
      const result = fn(...args);

      // Set the throttle flag
      throttleTimers.set(uniqueKey, true);

      await result;

      // Reset the flag after the specified delay
      setTimeout(() => {
        throttleTimers.set(uniqueKey, false);
      }, timeDelay);
    }
  };
}

export function debounceAsync<T extends (...args: any[]) => Promise<any | void>>(
  fn: T,
  identifier: string,
  timeDelay = 1500
): (...args: Parameters<T>) => Promise<Awaited<ReturnType<T>> | undefined> {
  let timerHandle: ReturnType<typeof setTimeout> | null = null;
  let currentReject: ((reason?: any) => void) | null = null;

  return (...args: Parameters<T>): Promise<Awaited<ReturnType<T>> | undefined> => {
    if (timerHandle) {
      clearTimeout(timerHandle);
      if (currentReject) {
        // Create a proper Error object that preserves stack trace
        const debounceError = new Error(`[${identifier}] Function call debounced`);
        debounceError.name = 'DebounceError';
        // Add a custom property to identify debounced errors
        (debounceError as any).aborted = true;
        (debounceError as any).identifier = identifier;
        currentReject(debounceError);
      }
    }

    return new Promise<Awaited<ReturnType<T>> | undefined>((resolve, reject) => {
      currentReject = reject;

      timerHandle = setTimeout(async () => {
        timerHandle = null;
        currentReject = null;

        try {
          const result = await fn(...args);
          resolve(result);
        } catch (error) {
          // Preserve the original error with additional context
          const enhancedError = error instanceof Error ? error : new Error(String(error));
          enhancedError.message = `[${identifier}] ${enhancedError.message}`;
          // Add the identifier to help with debugging
          (enhancedError as any).identifier = identifier;
          reject(enhancedError);
        }
      }, timeDelay);
    }).catch((error) => {
      // Check if this is a debounce error by checking the custom property
      if (error && typeof error === 'object' && (error as any).aborted) {
        // If the error is due to debouncing, we ignore it (or handle it quietly)
        return undefined;
      }
      // If it's a genuine error, preserve the original error context
      throw error;
    });
  };
}

export function debouncePromise<T extends (...args: any[]) => any>(
	fn: T,
	timeDelay = 1500
): (...args: Parameters<T>) => Promise<Awaited<ReturnType<T>>> {
	let fnTimer: Timeout | null = null;
	let resolveFn: (value: Awaited<ReturnType<T>>) => void;
	let rejectFn: (reason?: unknown) => void;

	return (...args: Parameters<T>): Promise<Awaited<ReturnType<T>>> => {
		return new Promise<Awaited<ReturnType<T>>>((resolve, reject) => {
			resolveFn = resolve;
			rejectFn = reject;

			if (fnTimer) {
				clearTimeout(fnTimer);
			}

			fnTimer = setTimeout(async () => {
				try {
					const result = await fn(...args);
					resolveFn(result);
				} catch (error) {
					rejectFn(error);
				}
			}, timeDelay);
		});
	};
}

const cache = new LRUCache<string, any>({
  max: 1000,
  ttl: 1000 * 60 * 5 // Default 5 minutes
});

export function cachedAsync<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  cacheKey: string,
  ttl = 5000
): (...args: Parameters<T>) => Promise<Awaited<ReturnType<T>>> {
  return async (...args: Parameters<T>): Promise<Awaited<ReturnType<T>>> => {
    const cached = cache.get(cacheKey);

    if (cached !== undefined) {
      return cached;
    }

    try {
      const result = await fn(...args);
      cache.set(cacheKey, result, { ttl });
      return result;
    } catch (error) {
      cache.delete(cacheKey);
      throw error;
    }
  };
}
